/**
 * Captcha — RC 1.20A inline human check.
 *
 * Self-contained, no external service / API keys. Shows a randomly
 * generated 4-char alphanumeric challenge on a subtly noisy canvas.
 * Calls `onChange(boolean)` whenever the pass/fail state flips.
 *
 * Not intended to defeat determined bots — it is a cheap friction
 * layer consistent with the brief ("signup button must remain disabled
 * until CAPTCHA is completed successfully"). If you want hCaptcha /
 * Cloudflare Turnstile later, this component can be swapped out
 * behind the same `onChange(boolean)` contract.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

function generate() {
  // Ambiguous-char-free set: no O/0/I/1/l
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 4; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

function drawChallenge(canvas, code) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);
  // Subtle dark background tint
  ctx.fillStyle = 'rgba(14,20,33,1)';
  ctx.fillRect(0, 0, width, height);
  // Gold dust + blue grid flecks for visual texture
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = i % 3 === 0
      ? 'rgba(201,164,92,0.35)'
      : 'rgba(91,124,255,0.25)';
    ctx.fillRect(
      Math.random() * width, Math.random() * height,
      Math.random() < 0.8 ? 1 : 2,
      Math.random() < 0.8 ? 1 : 2,
    );
  }
  // A faint ink line to defeat simple OCR
  ctx.strokeStyle = 'rgba(165,180,208,0.4)';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, height * (0.3 + Math.random() * 0.4));
  ctx.bezierCurveTo(
    width * 0.3, height * Math.random(),
    width * 0.6, height * Math.random(),
    width, height * (0.3 + Math.random() * 0.4),
  );
  ctx.stroke();
  // The actual code, letter by letter with tiny rotation + jitter
  ctx.textBaseline = 'middle';
  for (let i = 0; i < code.length; i++) {
    const x = 14 + i * 28;
    const y = height / 2 + (Math.random() - 0.5) * 6;
    const angle = (Math.random() - 0.5) * 0.35;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = i % 2 === 0 ? '#E8EEF9' : '#C9A45C';
    ctx.font = '600 24px "Cormorant Garamond", serif';
    ctx.fillText(code[i], 0, 0);
    ctx.restore();
  }
}

export default function Captcha({ onChange, testId = 'signup-captcha' }) {
  const canvasRef = useRef(null);
  const [code, setCode] = useState(() => generate());
  const [entered, setEntered] = useState('');
  const [isValid, setIsValid] = useState(false);

  const redraw = useCallback((nextCode) => {
    const c = canvasRef.current;
    if (c) drawChallenge(c, nextCode);
  }, []);

  useEffect(() => { redraw(code); }, [code, redraw]);

  useEffect(() => {
    const match =
      entered.trim().length === code.length &&
      entered.trim().toUpperCase() === code;
    if (match !== isValid) {
      setIsValid(match);
      if (onChange) onChange(match);
    }
  }, [entered, code, isValid, onChange]);

  const refresh = () => {
    setEntered('');
    setCode(generate());
    if (onChange) onChange(false);
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <label className="block text-xs uppercase tracking-[0.16em] text-ndor-ink-secondary font-mono-ui">
        Human check
      </label>
      <div className="flex items-stretch gap-2">
        <canvas
          ref={canvasRef}
          width={140}
          height={44}
          className="rounded-md border border-ndor-edge bg-ndor-surface-alt"
          aria-label="CAPTCHA challenge image"
          data-testid={`${testId}-canvas`}
        />
        <button
          type="button"
          onClick={refresh}
          aria-label="Refresh challenge"
          title="New challenge"
          data-testid={`${testId}-refresh`}
          className="px-2 rounded-md border border-ndor-edge bg-ndor-surface-alt text-ndor-ink-secondary hover:text-ndor-ink hover:border-ndor-gold/50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <input
          type="text"
          value={entered}
          onChange={(e) => setEntered(e.target.value.slice(0, 6))}
          placeholder="Enter code"
          autoComplete="off"
          maxLength={6}
          aria-label="Type the CAPTCHA code"
          data-testid={`${testId}-input`}
          className={`flex-1 min-w-0 rounded-md border bg-ndor-surface-alt px-3 text-sm text-ndor-ink placeholder:text-ndor-ink-muted focus:outline-none transition-colors ${
            isValid
              ? 'border-ndor-success/60 ring-1 ring-ndor-success/40'
              : 'border-ndor-edge focus:border-ndor-gold/40'
          }`}
        />
      </div>
      {!isValid && entered.length >= code.length && (
        <p className="text-xs text-ndor-error">Code does not match — try again.</p>
      )}
    </div>
  );
}
